"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const features = [
{
  icon: "ClipboardDocumentListIcon",
  title: "Instant pet profiles on arrival",
  desc: "When a client books through DogFob, you receive their pet's complete profile — breed, age, allergies, medications, behavioral notes — before the appointment. No intake forms. No surprises.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1373c31da-1764659090238.png",
  imageAlt: "Veterinarian reviewing pet health information on a tablet at a modern clinic desk",
  stat: "Save 8 min per client",
  statColor: "#5EBFB3",
  reverse: false
},
{
  icon: "BellIcon",
  title: "Automated client reminders",
  desc: "Send vaccine due alerts, appointment reminders, and post-visit follow-ups automatically. Clients get a notification in the DogFob app and by email — reducing no-shows by up to 34%.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bbbe35cd-1766709251678.png",
  imageAlt: "Person looking at a phone notification from a pet care app showing an appointment reminder",
  stat: "34% fewer no-shows",
  statColor: "#7C3AED",
  reverse: true
},
{
  icon: "GiftIcon",
  title: "Loyalty rewards that work",
  desc: "Create custom loyalty programs through DogFob — points per visit, discounts for referrals, or milestone rewards. Clients see their points balance in the app, giving them a reason to come back.",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a5581b11-1765207007742.png",
  imageAlt: "Happy customer at a pet store counter receiving a loyalty reward notification on their smartphone",
  stat: "2.4× more repeat visits",
  statColor: "#D97706",
  reverse: false
}];


export default function PartnerFeaturesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.
            querySelectorAll(".scroll-fade-up, .scroll-fade-left, .scroll-fade-right").
            forEach((el, i) => {
              setTimeout(() => el.classList.add("is-visible"), i * 90);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" ref={sectionRef} className="py-24 bg-white dark:bg-[#0f172a]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        {/* Header */}
        <div className="text-center mb-20 scroll-fade-up">
          <span className="teal-badge mb-4">Partner Features</span>
          <h2 className="text-4xl sm:text-5xl font-extrabold text-[#1A2B2B] dark:text-slate-100 tracking-tight mt-4 mb-5">
            Tools built for pet professionals
          </h2>
          <p className="text-[#4A6363] dark:text-slate-400 text-lg max-w-xl mx-auto leading-relaxed">
            DogFob gives every type of pet business the client intelligence and automation they need to deliver better care and grow faster.
          </p>
        </div>

        {/* Feature rows */}
        <div className="flex flex-col gap-24">
          {features.map((feature, idx) =>
          <div
            key={feature.title}
            className={`grid lg:grid-cols-2 gap-14 items-center ${
            feature.reverse ? "lg:[&>*:first-child]:order-2 lg:[&>*:last-child]:order-1" : ""}`
            }>
            
              {/* Image side */}
              <div className={feature.reverse ? "scroll-fade-right" : "scroll-fade-left"}>
                <div
                className="rounded-3xl overflow-hidden relative"
                style={{ boxShadow: "0 24px 64px rgba(14,165,160,0.14)" }}>
                
                  <AppImage
                  src={feature.image}
                  alt={feature.imageAlt}
                  width={600}
                  height={420}
                  className="w-full h-[380px] object-cover" />
                
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0F2027]/20 to-transparent" />
                  {/* Stat badge */}
                  <div
                  className="absolute bottom-5 left-5 glass-card rounded-xl px-4 py-2.5"
                  style={{ boxShadow: "0 8px 24px rgba(15,32,39,0.12)" }}>
                  
                    <div className="flex items-center gap-2">
                      <Icon name="ArrowTrendingUpIcon" size={16} style={{ color: feature.statColor }} />
                      <span
                      className="text-sm font-extrabold font-heading"
                      style={{ color: feature.statColor }}>
                      
                        {feature.stat}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Copy side */}
              <div
              className={`flex flex-col gap-5 ${
              feature.reverse ? "scroll-fade-left" : "scroll-fade-right"}`
              }>
              
                <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center transition-all duration-300 hover:scale-[1.02] hover:-translate-y-1 hover:shadow-xl"
                style={{ background: "#E8F7F6" }}>
                
                  <Icon
                  name={feature.icon as "ClipboardDocumentListIcon"}
                  size={22}
                  className="text-teal-500" />
                
                </div>
                <h3 className="text-3xl font-extrabold text-[#1A2B2B] dark:text-slate-100 tracking-tight font-heading leading-tight">
                  {feature.title}
                </h3>
                <p className="text-[#4A6363] dark:text-slate-400 leading-relaxed text-lg">{feature.desc}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}